import { FormEvent, useEffect, useState } from "react";
import { Bell, BellOff, CalendarCheck2, Plus, Trash2 } from "lucide-react";

import { leaveApi } from "../api";
import { useToast } from "#/components/common/ToastProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { useAsyncData, useMutation } from "../hooks";
import type {
  EligibleLeaveType,
  LeavePlan,
  LeavePlanCreateRequest,
  LeavePlanStatus,
} from "../types";

const PLAN_YEAR = new Date().getFullYear();

const STATUS_COLORS: Record<LeavePlanStatus, string> = {
  PLANNED:   "bg-blue-100 text-blue-700",
  SUBMITTED: "bg-emerald-100 text-emerald-700",
  CANCELLED: "bg-slate-200 text-slate-500",
};

export default function LeavePlansPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [year, setYear] = useState(PLAN_YEAR);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<LeavePlan | null>(null);

  const plans = useAsyncData(() => leaveApi.myLeavePlans(year), [year]);
  const eligibleTypes = useAsyncData(() => leaveApi.eligibleLeaveTypes(), []);

  const save = useMutation(async (payload: LeavePlanCreateRequest) => {
    if (editingPlan) return leaveApi.updateLeavePlan(editingPlan.id, payload);
    return leaveApi.createLeavePlan(payload);
  });

  const cancelPlan = useMutation((id: number) => leaveApi.cancelLeavePlan(id));

  async function handleSubmit(payload: LeavePlanCreateRequest) {
    await save.mutate(payload);
    setShowForm(false);
    setEditingPlan(null);
    await plans.reload();
  }

  async function handleCancel(plan: LeavePlan) {
    if (!(await confirm(`Cancel the planned leave "${plan.leaveTypeName}" starting ${plan.startDate}?`))) return;
    await cancelPlan.mutate(plan.id);
    await plans.reload();
  }

  function openEdit(plan: LeavePlan) {
    setEditingPlan(plan);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingPlan(null);
  }

  const activePlans = plans.data?.filter((p) => p.status !== "CANCELLED") ?? [];
  const cancelledPlans = plans.data?.filter((p) => p.status === "CANCELLED") ?? [];

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="My Leave Plans"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <select
              className="rounded-xl border px-3 py-2 text-sm font-semibold outline-none focus:border-emerald-500"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {[PLAN_YEAR - 1, PLAN_YEAR, PLAN_YEAR + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => { setEditingPlan(null); setShowForm(true); }}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
            >
              <Plus className="h-4 w-4" />
              Add Plan
            </button>
          </div>
        }
      />

      {plans.error && <LeaveError message={plans.error} onRetry={plans.reload} />}
      {save.error && <LeaveError message={save.error} />}
      {cancelPlan.error && <LeaveError message={cancelPlan.error} />}

      {/* Plan form */}
      {showForm && (
        <PlanForm
          initialValue={editingPlan}
          planYear={year}
          eligibleTypes={eligibleTypes.data ?? []}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      {plans.loading ? <LeaveLoading label="Loading leave plans…" /> : null}

      {/* Active plans */}
      {activePlans.length > 0 ? (
        <div className="rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-5 py-3">
            <h2 className="font-black text-slate-900">Active Plans ({activePlans.length})</h2>
          </div>
          <div className="divide-y">
            {activePlans.map((plan) => (
              <PlanRow
                key={plan.id}
                plan={plan}
                onEdit={openEdit}
                onCancel={handleCancel}
                cancelling={cancelPlan.loading}
              />
            ))}
          </div>
        </div>
      ) : !plans.loading ? (
        <div className="rounded-2xl border bg-white p-10 text-center shadow-sm">
          <CalendarCheck2 className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-500">No leave plans for {year}</p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
          >
            <Plus className="h-4 w-4" />
            Add your first plan
          </button>
        </div>
      ) : null}

      {/* Cancelled plans (collapsed) */}
      {cancelledPlans.length > 0 && (
        <details className="rounded-2xl border bg-slate-50">
          <summary className="cursor-pointer px-5 py-3 text-sm font-bold text-slate-500 select-none">
            Cancelled Plans ({cancelledPlans.length})
          </summary>
          <div className="divide-y border-t">
            {cancelledPlans.map((plan) => (
              <PlanRow key={plan.id} plan={plan} onEdit={openEdit} onCancel={handleCancel} cancelling={false} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ─── Plan row ─────────────────────────────────────────────────────────────────

function PlanRow({
  plan,
  onEdit,
  onCancel,
  cancelling,
}: {
  plan: LeavePlan;
  onEdit: (p: LeavePlan) => void;
  onCancel: (p: LeavePlan) => void;
  cancelling: boolean;
}) {
  const isCancelled = plan.status === "CANCELLED";

  function fmtDate(v: string) {
    try {
      return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch { return v; }
  }

  return (
    <div className={`flex flex-wrap items-center justify-between gap-4 px-5 py-4 ${isCancelled ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <span className="text-xs font-black leading-none">
            {new Date(`${plan.startDate}T00:00:00`).toLocaleDateString(undefined, { month: "short" }).toUpperCase()}
          </span>
          <span className="text-sm font-black leading-none">
            {new Date(`${plan.startDate}T00:00:00`).getDate()}
          </span>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-black text-slate-900">{plan.leaveTypeName}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_COLORS[plan.status]}`}>
              {plan.status}
            </span>
          </div>
          <div className="mt-0.5 text-sm text-slate-500">
            {fmtDate(plan.startDate)} → {fmtDate(plan.endDate)} · {plan.plannedDays} day(s)
          </div>
          {plan.notes && <div className="mt-0.5 text-xs text-slate-400">{plan.notes}</div>}
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {plan.reminderEnabled && (
          <span className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-600" title={`Reminder ${plan.reminderDaysBefore ?? 7} days before`}>
            <Bell className="h-3 w-3" />
            {plan.reminderDaysBefore}d
          </span>
        )}

        {!isCancelled && (
          <>
            <button
              type="button"
              onClick={() => onEdit(plan)}
              className="rounded-lg border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
            >
              Edit
            </button>
            <button
              type="button"
              disabled={cancelling}
              onClick={() => onCancel(plan)}
              className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 className="h-3 w-3" />
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Plan form ────────────────────────────────────────────────────────────────

function PlanForm({
  initialValue,
  planYear,
  eligibleTypes,
  submitting,
  onSubmit,
  onCancel,
}: {
  initialValue?: LeavePlan | null;
  planYear: number;
  eligibleTypes: EligibleLeaveType[];
  submitting?: boolean;
  onSubmit: (payload: LeavePlanCreateRequest) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<LeavePlanCreateRequest & { planYear: number }>({
    leaveTypeId: 0,
    startDate: "",
    endDate: "",
    notes: "",
    reminderEnabled: false,
    reminderDaysBefore: 14,
    planYear,
  });

  useEffect(() => {
    if (initialValue) {
      setForm({
        leaveTypeId: initialValue.leaveTypeId,
        startDate: initialValue.startDate,
        endDate: initialValue.endDate,
        notes: initialValue.notes ?? "",
        reminderEnabled: initialValue.reminderEnabled,
        reminderDaysBefore: initialValue.reminderDaysBefore ?? 14,
        planYear,
      });
    }
  }, [initialValue, planYear]);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.leaveTypeId) { toast.error("Select a leave type."); return; }
    if (!form.startDate || !form.endDate) { toast.error("Select start and end dates."); return; }
    if (form.endDate < form.startDate) { toast.error("End date cannot be before start date."); return; }

    // Rough day count
    const start = new Date(`${form.startDate}T00:00:00`);
    const end = new Date(`${form.endDate}T00:00:00`);
    const plannedDays = Math.ceil((end.getTime() - start.getTime()) / 86400000) + 1;

    await onSubmit({
      leaveTypeId: form.leaveTypeId,
      startDate: form.startDate,
      endDate: form.endDate,
      notes: form.notes?.trim() || null,
      reminderEnabled: form.reminderEnabled,
      reminderDaysBefore: form.reminderEnabled ? (form.reminderDaysBefore ?? 14) : null,
    });
    void plannedDays; // used by backend
  }

  return (
    <form
      onSubmit={submit}
      className="grid gap-4 rounded-3xl border bg-white p-5 shadow-sm md:grid-cols-2"
    >
      <div className="md:col-span-2">
        <h3 className="font-black text-slate-900">{initialValue ? "Edit Plan" : "New Leave Plan"}</h3>
        <p className="mt-0.5 text-sm text-slate-500">Plans are informational only — no balance is reserved.</p>
      </div>

      <Field label="Leave Type" required>
        <select
          className="input"
          value={form.leaveTypeId || ""}
          onChange={(e) => set("leaveTypeId", Number(e.target.value))}
          required
        >
          <option value="">Select leave type</option>
          {eligibleTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} — {t.remainingDays} day(s) remaining
            </option>
          ))}
        </select>
      </Field>

      <div /> {/* spacer */}

      <Field label="Start Date" required>
        <input
          type="date"
          className="input"
          value={form.startDate}
          onChange={(e) => set("startDate", e.target.value)}
          required
        />
      </Field>

      <Field label="End Date" required>
        <input
          type="date"
          className="input"
          value={form.endDate}
          min={form.startDate || undefined}
          onChange={(e) => set("endDate", e.target.value)}
          required
        />
      </Field>

      <div className="md:col-span-2">
        <Field label="Notes">
          <textarea
            className="input min-h-20"
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Optional notes about this planned leave…"
          />
        </Field>
      </div>

      {/* Reminder toggle */}
      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border p-4 md:col-span-2">
        <input
          type="checkbox"
          checked={form.reminderEnabled}
          onChange={(e) => set("reminderEnabled", e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded"
        />
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-700">
            {form.reminderEnabled ? (
              <Bell className="h-4 w-4 text-blue-500" />
            ) : (
              <BellOff className="h-4 w-4 text-slate-400" />
            )}
            Enable reminder
          </div>
        </div>
      </label>

      {form.reminderEnabled && (
        <Field label="Remind me (days before)">
          <input
            type="number"
            className="input"
            min={1}
            max={90}
            value={form.reminderDaysBefore ?? 14}
            onChange={(e) => set("reminderDaysBefore", Number(e.target.value))}
          />
        </Field>
      )}

      <div className="flex justify-end gap-2 md:col-span-2">
        <button type="button" onClick={onCancel} className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-emerald-700 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {submitting ? "Saving…" : initialValue ? "Update Plan" : "Create Plan"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="space-y-1.5">
      <div className="text-sm font-semibold text-slate-700">
        {label}{required && <span className="ml-1 text-red-500">*</span>}
      </div>
      {children}
    </label>
  );
}
